import React from 'react';
import { useParams, Link } from 'react-router-dom';
import MainLayout from '../layouts/MainLayout';
import { Card, CardContent } from '../components/Card';
import Button from '../components/Button';
import { getServiceBySlug, getRelatedServices } from '../data/services';
import { ArrowRight, CheckCircle2, ArrowLeft } from 'lucide-react';
import { generateServiceSchema } from '../lib/seo';

const ServiceDetailPage = () => {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);
  const relatedServices = getRelatedServices(slug);

  if (!service) {
    return (
      <MainLayout title="Leistung nicht gefunden">
        <div className="section-container text-center">
          <h1 className="text-3xl font-bold text-primary-text-dark mb-4">
            Leistung nicht gefunden
          </h1>
          <p className="text-primary-text-muted mb-6">
            Die gesuchte Leistung existiert nicht.
          </p>
          <Link to="/leistungen">
            <Button variant="primary">Zurück zu allen Leistungen</Button>
          </Link>
        </div>
      </MainLayout>
    );
  }

  const structuredData = generateServiceSchema(service);

  return (
    <MainLayout
      title={`${service.title} | SENKAY Personal und Logistik`}
      description={service.description}
      structuredData={structuredData}
    >
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-primary-skin/30 to-white py-16 overflow-hidden">
        {/* Background Image */}
        {service.image && (
          <div className="absolute inset-0">
            <img
              src={service.image}
              alt={service.title}
              className="w-full h-full object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-white/30 via-white/20 to-white/10"></div>
          </div>
        )}

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/leistungen" className="inline-flex items-center text-primary-text-dark hover:text-primary-blue-mid mb-6 font-medium backdrop-blur-sm bg-white/40 px-4 py-2.5 rounded-lg shadow-md">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zurück zu allen Leistungen
          </Link>

          <div className="max-w-4xl backdrop-blur-md bg-white/40 rounded-2xl p-10 md:p-12 shadow-2xl border border-white/50">
            <div className="inline-block px-4 py-2 rounded-full bg-primary-blue-light text-primary-blue-mid text-sm font-semibold mb-4 shadow-md">
              {service.category}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-primary-text-dark mb-6 hyphens-auto break-words" lang="de">
              {service.title}
            </h1>
            <p className="text-xl text-gray-700 font-medium">
              {service.description}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-container">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Main Column */}
          <div className="lg:col-span-2 space-y-12">
            {/* Features */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-primary-text-dark mb-6">
                Leistungsmerkmale
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {service.features.map((feature, index) => (
                  <Card key={index} hover={false} className="h-full">
                    <CardContent className="flex flex-col items-center text-center p-5">
                      <div className="w-12 h-12 rounded-full bg-primary-blue-light flex items-center justify-center mb-3">
                        <CheckCircle2 className="w-6 h-6 text-primary-blue-mid" />
                      </div>
                      <p className="text-sm font-medium text-primary-text-dark leading-snug">
                        {feature}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Benefits */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-primary-text-dark mb-6">
                Ihre Vorteile
              </h2>
              <div className="space-y-4">
                {service.benefits.map((benefit, index) => (
                  <Card key={index} hover={false}>
                    <CardContent className="flex items-start space-x-4">
                      <div className="w-10 h-10 rounded-full bg-primary-green-light flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 className="w-5 h-5 text-green-700" />
                      </div>
                      <p className="text-primary-text-dark pt-2">{benefit}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Process */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-primary-text-dark mb-6">
                So läuft es ab
              </h2>
              <div className="space-y-4">
                {service.processSteps.map((step, index) => (
                  <Card key={index} hover={false}>
                    <CardContent className="flex items-start space-x-4">
                      <div className="w-10 h-10 rounded-full bg-primary-blue-mid text-white flex items-center justify-center font-semibold flex-shrink-0">
                        {index + 1}
                      </div>
                      <p className="text-primary-text-dark pt-2">{step}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              {/* CTA Card */}
              <Card>
                <CardContent className="text-center">
                  <h3 className="text-xl font-semibold text-primary-text-dark mb-4">
                    Interesse an dieser Leistung?
                  </h3>
                  <p className="text-sm text-primary-text-muted mb-6">
                    Fordern Sie jetzt ein unverbindliches Angebot an.
                  </p>
                  <Link to="/kontakt" state={{ serviceType: service.id }}>
                    <Button variant="primary" className="w-full mb-3" icon={ArrowRight} iconPosition="right">
                      Angebot anfordern
                    </Button>
                  </Link>
                  <a href="tel:+4917762766603">
                    <Button variant="secondary" className="w-full">
                      Jetzt anrufen
                    </Button>
                  </a>
                </CardContent>
              </Card>

              {/* Target Audience */}
              <Card>
                <CardContent>
                  <h4 className="font-semibold text-primary-text-dark mb-3">
                    Zielgruppen
                  </h4>
                  <p className="text-sm text-primary-text-muted">
                    {service.targetAudience}
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      {relatedServices.length > 0 && (
        <section className="section-container bg-primary-skin/20">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-primary-text-dark mb-12">
            Weitere passende Leistungen
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {relatedServices.map((related) => (
              <Card key={related.id}>
                <CardContent>
                  <h3 className="text-xl font-semibold text-primary-text-dark mb-3">
                    {related.title}
                  </h3>
                  <p className="text-sm text-primary-text-muted mb-4">
                    {related.shortDescription}
                  </p>
                  <Link to={`/leistungen/${related.slug}`}>
                    <Button variant="ghost" className="w-full">
                      Mehr erfahren
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}
    </MainLayout>
  );
};

export default ServiceDetailPage;
